import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SignIn } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * AuthGuard - A component that protects routes requiring authentication
 * 
 * This component ensures that the children are only rendered when the user is authenticated.
 * It shows a loading state when checking authentication and a sign-in form when not authenticated.
 * The component is designed with accessibility in mind, avoiding issues with focus management.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children, fallback }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin mb-4" aria-hidden="true" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Welcome to Keyword OS</h1>
            <p className="text-muted-foreground">
              Please sign in to access keyword research tools
            </p>
          </div>
          <SignIn 
            routing="hash"
            signUpUrl="/sign-up"
            afterSignInUrl="/app/dashboard"
            appearance={{
              elements: {
                formButtonPrimary: 'bg-primary hover:bg-primary/90',
                card: 'shadow-lg border-border',
              }
            }}
          />
        </div>
      </div>
    );
  }

  // When authenticated, render children directly without additional wrapper divs
  // that might interfere with other layouts
  return <>{children}</>;
};
